import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding, useContentToken } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Full-width photo slider
//
// A single image spanning the group's width, auto-advancing with prev/next
// arrows on hover. No dot indicators or photo counter — the image is the
// only chrome-free element in the group.
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  const safeCurrent = Math.min(current, count - 1);

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === safeCurrent ? ' amenities-slider-img--active' : ''}`}
          sizes="(max-width: 767px) 92vw, min(30rem, 44vw)"
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Amenity group: full-width carousel, heading below it, then the full list.
// ============================================================================

interface AmenityGroupProps {
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
}

function AmenityGroup({ headingNode, headingText, items, photos }: AmenityGroupProps) {
  const groupRef = useScrollReveal<HTMLDivElement>();
  const hasImage = photos.length > 0;

  return (
    <div ref={groupRef} className="reveal-fade-up amenities-group">
      {hasImage && (
        <div className="amenities-group-gallery">
          <PhotoSlider photos={photos} heading={headingText} />
        </div>
      )}

      {headingNode}

      <ul className="amenities-col-list">
        {items.map((item, i) => (
          <li key={i} className="amenities-col-item">
            {item.title || ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

// Strip HTML tags from a richtext value so it can be used as a plain
// string (e.g. as fallback alt text on a photo slider).
function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

export function AmenitiesList() {
  const communityHeadingRaw = useContentToken('amenitieslist.communityHeading');
  const apartmentHeadingRaw = useContentToken('amenitieslist.apartmentHeading');
  const communityHeadingText = stripHtml(communityHeadingRaw) || 'Community Amenities';
  const apartmentHeadingText = stripHtml(apartmentHeadingRaw) || 'Interior Features';

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="pt-2 md:pt-4 pb-10 md:pb-16 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasContent ? (
          <div className="amenities-stack">
            {communityItems.length > 0 && (
              <AmenityGroup
                headingNode={
                  <RichTextContent
                    path="amenitieslist.communityHeading"
                    label="Community Heading"
                    defaultValue="<p>Community Amenities</p>"
                    as="h3"
                    className="amenities-col-heading"
                  />
                }
                headingText={communityHeadingText}
                items={communityItems}
                photos={communityPhotos}
              />
            )}

            {apartmentItems.length > 0 && (
              <AmenityGroup
                headingNode={
                  <RichTextContent
                    path="amenitieslist.apartmentHeading"
                    label="Apartment Heading"
                    defaultValue="<p>Interior Features</p>"
                    as="h3"
                    className="amenities-col-heading"
                  />
                }
                headingText={apartmentHeadingText}
                items={apartmentItems}
                photos={apartmentPhotos}
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        /* Two groups side by side, each pulled toward the centre gutter so the
           galleries read as a pair rather than as two edge-anchored columns. */
        .amenities-stack {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          align-items: start;
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .amenities-stack { gap: 1.5rem; }
        }
        @media (max-width: 767px) {
          .amenities-stack {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
        }

        /* One shared content width for the gallery, heading and list, so
           nothing in the group can overhang the photo above it. */
        .amenities-group {
          display: flex;
          flex-direction: column;
          width: 100%;
          max-width: 30rem;
          padding: 0 0 1.75rem;
        }
        .amenities-stack > .amenities-group:first-child { justify-self: end; }
        .amenities-stack > .amenities-group:last-child { justify-self: start; }
        .amenities-stack > .amenities-group:only-child { justify-self: center; }
        @media (max-width: 767px) {
          .amenities-stack > .amenities-group { justify-self: center; }
        }

        /* Section sub-heading — sits below the gallery. Matches the HeroSolidBG
           overline: site body face, normal weight, wide tracking. */
        .amenities-col-heading {
          font-family: var(--vibe-font-body);
          font-size: clamp(1.25rem, 2.2vw, 2rem);
          font-weight: 400;
          letter-spacing: 0.05em;
          color: var(--vibe-color-text);
          text-align: center;
          margin: 0 0 1.25rem;
        }

        /* Carousel sitting above each heading, spanning the group width that
           the heading and list are also bounded by. */
        .amenities-group-gallery {
          width: 100%;
          margin: 0 auto 1.5rem;
        }

        .amenities-slider {
          position: relative;
          width: 100%;
          /* Landscape — noticeably shorter than the near-square it replaced. */
          aspect-ratio: 4 / 3;
          overflow: hidden;
          background: #000;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.4);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.6);
        }
        .amenities-slider-arrow--prev { left: 14px; }
        .amenities-slider-arrow--next { right: 14px; }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        /* Left-aligned list flowing below the carousel. The native marker stays
           off so the square drawn in ::before can be sized freely WITHOUT
           inflating each row's line height. */
        .amenities-col-list {
          list-style: none;
          padding: 0;
          margin: 0;
          width: 100%;
          min-width: 0;
        }

        .amenities-col-item {
          position: relative;
          padding: 0.15rem 0 0.15rem 1.1rem;
          text-align: left;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          color: var(--vibe-color-text);
          line-height: 1.35;
        }
        .amenities-col-item::before {
          content: "";
          position: absolute;
          left: 0;
          /* Center the square on the first text line (0.15rem item padding +
             half of the 1.35 line-height). */
          top: calc(0.15rem + 0.675em);
          transform: translateY(-50%);
          /* The square itself — bump this value to resize the bullet. */
          width: 0.325em;
          height: 0.325em;
          background: currentColor;
        }
        @media (max-width: 900px) {
          .amenities-col-item { font-size: 0.9rem; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'richtext',
      defaultValue: '<p>Interior Features</p>',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
